<template>
  <div class="log">
    <Spinner color="blue" v-if="isLoading" style="padding-bottom: 50px"></Spinner>
    <div class="log-list" v-else>
      <h4 class="text-explainer" v-if="sortedActivities.length < 1">{{i18n[locale].there_is_no_logged_activity}}</h4>
      <ul>
        <li class="activity" v-for="activity in packedActivities" :key="activity.at">
          <div class="activity__headline">
            <div class="user" v-if="usersHash[activity.by.id]">
              <img :src="getUserImg(activity)"/>
              <h4>{{getUserName(activity)}}</h4>
            </div>
            <p class="activity__headline__timestamp hint--top-left" :aria-label="formatDate(activity.at)">
              {{ getDuration(activity.at)}} {{i18n[locale].ago}}
            </p>
          </div>
          <ul class="activity-changes">
            <li class="activity-changes__change"
                v-for="change in activity.changes"
                :key="change.at">
              <div v-html="humanizeChange(change)"></div>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
  import Spinner from '../common/Spinner'
  import docendo from '../../docendo'
  import moment from 'moment'

  export default {
    components: {Spinner},
    props: {
      subject: {
        type: Object
      }
    },
    data () {
      return {
        i18n: {
          da: {
            there_is_no_logged_activity: 'Der er ingen historik',
            no_changes: 'Ingen ændringer foretaget',
            created_subject: 'Oprettede faget',
            from: 'fra',
            to: 'til',
            added: 'Tilføjede',
            added_user: 'Tilføjede underviser',
            added_room: 'Tilføjede lokalet',
            removed: 'Fjernede',
            removed_room: 'Fjernede lokalet',
            removed_user: 'Fjernede underviseren',
            changed: 'Ændrede',
            changed_hours_in_school_year_from: 'Ændrede timer i skoleåret fra',
            changed_subject_name_from: 'Ændrede fagets navn fra',
            changed_subject_default_duration_from: 'Ændrede fagets normale varighed fra',
            changed_subject_color: 'Ændrede fagets farve',
            changed_subject_type_from: 'Ændrede fagets type fra',
            subject: 'Fag',
            supplemental: 'UUV',
            frame: 'Temavisning (Ramme)',
            break: 'break',
            resource: 'Ressource',
            optional: 'Valgfag/Hold',
            ago: 'siden'
          },
          en: {
            there_is_no_logged_activity: 'No history',
            no_changes: 'No changes made',
            created_subject: 'created the subject',
            from: 'from',
            to: 'to',
            added: 'Added',
            added_user: 'Added teacher',
            added_room: 'Added room',
            removed: 'Removed',
            removed_room: 'Removed room',
            removed_user: 'Removed the teacher',
            changed: 'Changed',
            changed_hours_in_school_year_from: 'Changed annual isntruction time from',
            changed_subject_name_from: 'Changed the subject title from',
            changed_subject_default_duration_from: 'Changed the subject standard duration from',
            changed_subject_color: 'Changed the subject color',
            changed_subject_type_from: 'Changed the subject category from',
            subject: 'subject',
            supplemental: 'UUV',
            frame: 'Theme/focus',
            break: 'break',
            resource: 'Resource',
            optional: 'Elective',
            ago: 'ago'
          },
          nb: {
            there_is_no_logged_activity: 'Ingen historikk',
            no_changes: 'Ingen endringer er blitt gjort',
            created_subject: 'Opprettet faget',
            from: 'fra',
            to: 'til',
            added: 'La til',
            added_user: 'La til underviser',
            added_room: 'La til lokalet',
            removed: 'Fjernet',
            removed_room: 'Fjernet lokalet',
            removed_user: 'Fjernet underviseren',
            changed: 'Endret',
            changed_hours_in_school_year_from: 'Endret timer i skoleåret fra',
            changed_subject_name_from: 'Endret fagets navn fra',
            changed_subject_default_duration_from: 'Endret fagets normale varighet fra',
            changed_subject_color: 'Endret fagets farge',
            changed_subject_type_from: 'Endret fagets type fra',
            subject: 'Fag',
            supplemental: 'UUV',
            frame: 'Temavisning (Ramme)',
            break: 'Pause',
            resource: 'Ressurs',
            optional: 'Valgfag',
            ago: 'siden'
          }
        },
        isLoading: true,
        activities: null
      }
    },
    computed: {
      locale () {
        return this.$store.getters.locale
      },
      usersHash () {
        return this.$store.getters.allUsersInHash
      },
      sortedActivities () {
        let withoutNull = this.activities.filter(activity => activity)
        return withoutNull.sort((a, b) => new Date(b.at) - new Date(a.at))
      },
      packedActivities () {
        let sortedOnTimestamp = []
        for (let i = 0; i < this.sortedActivities.length; i++) {
          let lastSortedTimestamp = sortedOnTimestamp[sortedOnTimestamp.length - 1]
          // If it is within same minute
          let isWithinFiveMinutes = lastSortedTimestamp && moment(lastSortedTimestamp.at).format('YYYYMMDDh:mm') === moment(this.sortedActivities[i].at).format('YYYYMMDDh:mm')
          if (isWithinFiveMinutes && lastSortedTimestamp.by && lastSortedTimestamp.by.id === this.sortedActivities[i].by.id) {
            sortedOnTimestamp[sortedOnTimestamp.length - 1].changes.push(this.sortedActivities[i])
          } else {
            this.$set(this.sortedActivities[i], 'changes', [this.sortedActivities[i]])
            sortedOnTimestamp.push(this.sortedActivities[i])
          }
        }
        return sortedOnTimestamp
      }
    },
    methods: {
      humanizeChange (change) {
        // Teachers
        let text = ''
        if (change.ta === 'subject_calendars') {
          if (change.ac === 'I') {
            text += '<p><span class="icon-calendar4"></span> ' + this.i18n[this.locale]['added'] + ' ' + change.refs.calendar.name + '</p>'
          }
          if (change.ac === 'D') {
            text += '<p><span class="icon-calendar4"></span> ' + this.i18n[this.locale]['removed'] + ' ' + change.refs.calendar.name + '</p>'
          }
          // Rooms
        } else if (change.ta === 'subjects' && !change.new) {
          text += '<p>' + this.i18n[this.locale]['created_subject'] + '</p>'
          // Updated property
        } else if (change.ta === 'subjects' && change.new) {
          for (let prop in change.new) {
            if (prop === 'hours') {
              text += '<p>' + this.i18n[this.locale].changed_hours_in_school_year_from + ' ' + change.old.hours + ' ' + this.i18n[this.locale].to + '  ' + change.new.hours + '</p>'
            }
            if (prop === 'default_duration') {
              text += '<p>' + this.i18n[this.locale].changed_subject_default_duration_from + ' ' + change.old.default_duration + ' ' + this.i18n[this.locale].to + '  ' + change.new.default_duration + '</p>'
            }
            if (prop === 'name') {
              text += '<p>' + this.i18n[this.locale].changed_subject_name_from + ' ' + change.old.name + ' ' + this.i18n[this.locale].to + '  ' + change.new.name + '</p>'
            }
            if (prop === 'color') {
              text += '<p>' + this.i18n[this.locale].changed_subject_color + '</p>'
            }
            if (prop === 'subject_type') {
              text += '<p>' + this.i18n[this.locale].changed_subject_type_from + ' ' + this.i18n[this.locale][change.old.subject_type] + ' ' + this.i18n[this.locale].to + ' ' + this.i18n[this.locale][change.new.subject_type] + '</p>'
            }
          }
        }
        return text
      },
      formatDate (datetime) {
        moment.locale(this.locale)
        return moment(datetime).format('LLL')
      },
      getDuration (datetime) {
        moment.locale(this.locale)
        let diff = moment().diff(moment(datetime))
        return moment.duration(diff).humanize()
      },
      getSubjectLog () {
        const schoolId = this.subject.school_id
        docendo.auditSubject(schoolId, this.subject.id, data => {
          this.activities = data.hasOwnProperty('data') ? [] : JSON.parse(data)
          this.isLoading = false
        }, failure => {
          console.log('failed', failure)
        })
      },
      getUserImg (activity) {
        let userId = activity.by.id
        return this.usersHash[userId].default_thumb_url
      },
      getUserName (activity) {
        return activity.by ? activity.by.name : ''
      }
    },
    mounted () {
      this.getSubjectLog()
    }
  }
</script>

<style scoped>
  .log {
    margin-top: 10px;
  }

  .activity__headline {
    display: flex;
    justify-content: space-between;
  }

  .user {
    display: flex;
    margin-bottom: -20px;
  }

  .user > img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    margin-right: 5px;
  }

  .text-explainer {
    font-style: italic;
    color: #a2a3a6;
    padding: 20px 0;
  }

  .activity-changes {
    color: #a2a3a6;
    padding-left: 45px;
    margin-bottom: 10px;
    margin-top: 0px;
  }

  .activity__headline__timestamp {
    color: #a2a3a6;
  }
</style>



// WEBPACK FOOTER //
// SubjectFormLog.vue?6037b330